import { Flex, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import { countriesInfo, type Country } from '@/models/country';
import { Flag } from './Flag';
import { TeamDetailTrigger } from './teamDetail';
import { useTeamDetailOptional } from './teamDetail/TeamDetailContext';
import type { TeamDetailSource } from './teamDetail/teamDetailUtils';

/** Dismiss a parent modal before team detail opens; resume it when team detail closes. */
export type ParentModalTeamDetailHandoff = {
  onDismiss: () => void;
  onResume: () => void;
};

/** Matches fixture team rows (`FixtureListGrid`); ranks / headings sized up in `CountryListCard`. */
export function CountryListGrid(props: {
  countryList: Country[];
  svg?: boolean;
  /** When true, rows share parent height evenly (desktop profile My List card). */
  fillColumnHeight?: boolean;
  /** Tighter row for mobile profile stack (below xl). */
  compactMobile?: boolean;
  teamDetailSource?: TeamDetailSource;
  /** Avoid nested Chakra modals: dismiss parent, reopen via `onCloseResume` on team detail close. */
  parentModalHandoff?: ParentModalTeamDetailHandoff;
}) {
  const teamDetail = useTeamDetailOptional();
  const nameFontSize = useBreakpointValue(
    props.compactMobile ? { base: 14, sm: 15 } : { base: 16, sm: 17 },
  );
  const rankFontSize = useBreakpointValue(
    props.compactMobile ? { base: 18, sm: 20 } : { base: 21, sm: 22 },
  );
  const columnGap = useBreakpointValue(props.compactMobile ? { base: 4, sm: 6 } : { base: 6, sm: 6 });
  const flagW = useBreakpointValue(props.compactMobile ? { base: 6, sm: 7 } : { base: 7, sm: 8 });
  const n = props.countryList.length;
  const fill = Boolean(props.fillColumnHeight && n > 0);
  const source = props.teamDetailSource ?? 'profile-list';
  const handoff = props.parentModalHandoff;

  return (
    <VStack
      w='full'
      spacing={fill ? 3 : 2}
      h={fill ? '100%' : undefined}
      minH={fill ? '100%' : undefined}
      justify={fill ? 'space-between' : 'flex-start'}
      align='stretch'
    >
      {props.countryList.map((country, i) => (
        <TeamDetailTrigger
          key={country}
          country={country}
          source={source}
          rank={i + 1}
          variant='row'
          w='full'
          onOpen={
            handoff && teamDetail
              ? () => {
                  handoff.onDismiss();
                  teamDetail.openTeamDetail(country, {
                    source,
                    rank: i + 1,
                    onCloseResume: handoff.onResume,
                  });
                }
              : undefined
          }
        >
          <Flex w='full' align='center' columnGap={columnGap} minH={fill ? 0 : undefined} flex={fill ? 1 : undefined}>
            <Flex w={flagW} flexShrink={0} align='center' justify='center'>
              <Text fontWeight='semibold' fontSize={rankFontSize} lineHeight={fill ? 1.2 : 'short'}>
                {i + 1}
              </Text>
            </Flex>
            <Flex flex={1} align='center' justify='flex-start' minW={0} mr='0.18rem'>
              <Text
                fontSize={nameFontSize}
                fontWeight='semibold'
                lineHeight={fill ? 1.35 : 'short'}
                noOfLines={props.compactMobile ? 1 : 2}
                textAlign='left'
              >
                {countriesInfo[country].name}
              </Text>
            </Flex>
            <Flex flexShrink={0} align='center' justify='center' minH={6} minW={6}>
              <Flag country={country} w={flagW} borderRadius='3px' svg={props.svg} />
            </Flex>
          </Flex>
        </TeamDetailTrigger>
      ))}
    </VStack>
  );
}
